/**
 * The simulator's command box and filter panel describe the same thing: a migrate:team-style
 * command. A Draft is the panel's view of it; draftToCommand / commandToDraft keep the two in
 * step, so editing either one updates the other.
 */
import { commandFor, parseCommand } from "./engine.js";
import { arg, filtersToArgs, hasSelectionFlags, panelFromArgs, type SelectionFilters } from "./selection.js";

export const DRAFT_SCRIPTS = ["preview", "migrate:team", "migrate:all"] as const;
export type DraftScript = (typeof DRAFT_SCRIPTS)[number];

export const HR_REPORT_FILE = "Active Employee Report.xlsx";
export const DOCS_DIR = "legacy-docs";

export interface Draft {
  script:   DraftScript;
  dryRun:   boolean;
  hrReport: boolean;   // --hr-report "<file>" vs --no-hr-report (real runs need one of them)
  docs:     boolean;   // --docs-dir
  reset:    boolean;   // --reset: TRUNCATE first, and archive the memory
  filters:  SelectionFilters;
}

export const defaultDraft: Draft = { script: "preview", dryRun: false, hrReport: true, docs: false, reset: false, filters: {} };

export function draftToCommand(d: Draft): string {
  const args: string[] = [];
  if (d.dryRun) args.push("--dry-run");
  if (d.script !== "preview") args.push(d.hrReport ? `--hr-report="${HR_REPORT_FILE}"` : "--no-hr-report");
  if (d.docs) args.push(`--docs-dir=${DOCS_DIR}`);
  if (d.reset) args.push("--reset");
  const filterArgs = filtersToArgs(d.filters);
  // No filters means everyone; --all says so, instead of starting the interactive picker.
  args.push(...(filterArgs.length ? filterArgs : ["--all"]));
  return commandFor(d.script, args);
}

/** The panel's reading of a typed command, or null when it isn't a preview / migrate:team / migrate:all line. */
export function commandToDraft(line: string): Draft | null {
  const { script, args } = parseCommand(line);
  if (!(DRAFT_SCRIPTS as readonly string[]).includes(script)) return null;
  return {
    script: script as DraftScript,
    dryRun: args.includes("--dry-run"),
    hrReport: arg(args, "--hr-report") !== null || !args.includes("--no-hr-report"),
    docs: arg(args, "--docs-dir") !== null,
    reset: args.includes("--reset"),
    filters: hasSelectionFlags(args) ? panelFromArgs(args) : {},
  };
}
